import { memo, ReactNode, useMemo, useState } from 'react';
import { css } from '@emotion/react';
import { InsuranceType } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/shared/enums.pb';
import { InsuranceStatus } from '@weave/schema-gen-ts/dist/schemas/insurance-verification/v1/insurance_verification_adapter.pb';
import { useInvalidateQueries } from '@frontend/api-insurance-detail';
import { i18next, useTranslation } from '@frontend/i18n';
import { Icon, IconName } from '@frontend/icons';
import { PanelContent, PanelPortal } from '@frontend/panel-engine';
import { useMutation } from '@frontend/react-query-helpers';
import { useBreakpoint } from '@frontend/responsiveness';
import { SchemaInsuranceVerificationAdapterServiceV1 } from '@frontend/schema';
import { SurfaceHeader } from '@frontend/surface-header';
import { theme } from '@frontend/theme';
import { Button, ContentLoader, ListRow, ModalControlModalProps, Text, Tray, useAlert } from '@frontend/design-system';
import { useInsuranceType } from '../providers/insurance-type-provider';
import { usePersonInfoStore } from '../providers/person-info-provider';

const STATUS_LIST: { label: string; iconName: IconName; value: InsuranceStatus }[] = [
  {
    label: i18next.t('Active', { ns: 'insuranceDetails' }),
    value: InsuranceStatus.ACTIVE,
    iconName: 'shield-small',
  },
  {
    label: i18next.t('Self Pay', { ns: 'insuranceDetails' }),
    value: InsuranceStatus.SELF_PAY,
    iconName: 'insurance-self-pay-small',
  },
  {
    label: i18next.t('Inactive', { ns: 'insuranceDetails' }),
    value: InsuranceStatus.INACTIVE,
    iconName: 'insurance-uninsured-small',
  },
  {
    label: i18next.t('Unknown', { ns: 'insuranceDetails' }),
    value: InsuranceStatus.UNKNOWN,
    iconName: 'insurance-incomplete-small',
  },
];

interface Props extends ModalControlModalProps {
  verificationStatus?: InsuranceStatus;
  appointmentDate?: string;
  /**
   * Optional statuses to show in the panel. When omitted the full STATUS_LIST
   * is rendered. Callers (e.g. the eligibility list "More actions" menu) can
   * pass statuses like [SELF_PAY, INACTIVE].
   */
  availableStatuses?: InsuranceStatus[];
}

type ShellProps = ModalControlModalProps & { onClose: () => void; children: ReactNode };

const OverrideStatusPanelDesktopView = ({ onClose, children }: ShellProps) => {
  const { t } = useTranslation('insuranceDetails');

  return (
    <PanelPortal id='weave-insurance-verification' width='smallXL' onClose={onClose} enableAnimations>
      <SurfaceHeader.Root>
        <SurfaceHeader.Row>
          <SurfaceHeader.Title>{t('Override Insurance')}</SurfaceHeader.Title>
          <SurfaceHeader.Actions>
            <SurfaceHeader.Icon iconName='x' label={t('Close')} onClick={onClose} />
          </SurfaceHeader.Actions>
        </SurfaceHeader.Row>
      </SurfaceHeader.Root>
      <PanelContent css={panelContentCss}>{children}</PanelContent>
    </PanelPortal>
  );
};

const OverrideStatusTrayMobileView = ({ onClose, children, ...modalProps }: ShellProps) => {
  const { t } = useTranslation('insuranceDetails');
  return (
    <Tray {...modalProps} onClose={onClose} width='smallXL' css={{ padding: 0, gap: 0 }} disableCloseOnEscape>
      <SurfaceHeader.Root>
        <SurfaceHeader.Row>
          <SurfaceHeader.Title>{t('Override Insurance')}</SurfaceHeader.Title>
          <SurfaceHeader.Actions>
            <SurfaceHeader.Icon iconName='x' label={t('Close')} onClick={onClose} />
          </SurfaceHeader.Actions>
        </SurfaceHeader.Row>
      </SurfaceHeader.Root>
      <Tray.Body css={panelContentCss}>{children}</Tray.Body>
    </Tray>
  );
};

const OverrideStatusBody = memo(function OverrideStatusBody({
  verificationStatus,
  availableStatuses,
  appointmentDate,
  onClose,
}: {
  verificationStatus?: InsuranceStatus;
  availableStatuses?: InsuranceStatus[];
  appointmentDate?: string;
  onClose: () => void;
}) {
  const { t } = useTranslation('insuranceDetails');
  const alert = useAlert();
  const [selectedStatus, setSelectedStatus] = useState<InsuranceStatus | undefined>(verificationStatus);
  const {
    invalidateGetInsuranceByPersonIdQuery,
    invalidateGetInsuranceVerificationDetailsPersonIdQuery,
    invalidatePatientEligibilityListView,
  } = useInvalidateQueries();
  const { insuranceType } = useInsuranceType();
  const { personInfo, locationId } = usePersonInfoStore();
  const personId = personInfo?.PersonID as string;
  const isOnEligibilityListView = window.location.pathname.includes('schedule/eligibility-list');

  const overrideStatusMutation = useMutation({
    mutationFn: (params: { personId: string; insuranceStatus: InsuranceStatus; insuranceType: InsuranceType }) =>
      SchemaInsuranceVerificationAdapterServiceV1.OverrideInsuranceStatus(params),
    onSuccess() {
      alert.success(t('Insurance status has been successfully overridden.'));
      onClose();
      invalidateGetInsuranceByPersonIdQuery(personId, locationId!);
      invalidateGetInsuranceVerificationDetailsPersonIdQuery(personId, locationId!, insuranceType);
      if (isOnEligibilityListView && locationId && appointmentDate) {
        invalidatePatientEligibilityListView(locationId, appointmentDate, insuranceType);
      }
    },
    onError(error) {
      console.error('an error occurred while overriding insurance status', error);
      alert.error(t('An error occurred while overriding insurance status, Please try again.'));
    },
  });

  const handleSaveClick = () => {
    if (!selectedStatus) return;
    overrideStatusMutation.mutate({ personId, insuranceStatus: selectedStatus, insuranceType });
  };

  const isSaveDisabled = verificationStatus === selectedStatus || !selectedStatus;

  return (
    <>
      <ContentLoader show={overrideStatusMutation.isLoading} />
      <div css={statusListStyles}>
        {STATUS_LIST.filter(({ value }) => !availableStatuses || availableStatuses.includes(value)).map(
          ({ label, value, iconName }) => (
            <ListRow key={value} isSelected={selectedStatus === value} onClick={() => setSelectedStatus(value)}>
              <ListRow.Content css={statusRowContentStyles}>
                <Icon name={iconName} size={20} />
                <Text weight='bold' size='large'>
                  {label}
                </Text>
              </ListRow.Content>
            </ListRow>
          )
        )}
      </div>
      <div css={footerStyles}>
        <Button variant='secondary' onClick={onClose}>
          {t('Cancel')}
        </Button>
        <Button variant='primary' onClick={handleSaveClick} disabled={isSaveDisabled}>
          {t('Save')}
        </Button>
      </div>
    </>
  );
});

export const OverrideStatusSidePanel = ({
  verificationStatus,
  availableStatuses,
  appointmentDate,
  ...modalProps
}: Props) => {
  const size = useBreakpoint();
  const isDesktop = size === 'large' || size === 'xlarge';

  const body = useMemo(
    () => (
      <OverrideStatusBody
        verificationStatus={verificationStatus}
        availableStatuses={availableStatuses}
        appointmentDate={appointmentDate}
        onClose={modalProps.onClose}
      />
    ),
    [verificationStatus, availableStatuses, appointmentDate, modalProps.onClose]
  );

  /**
   * When openModal is triggered from useModalControl then modalProps.show is set to true and sidePanel is displayed.
   * When onClose is triggered from useModalControl then modalProps.show is set to false and sidepanel is not displayed.
   */
  if (!modalProps.show) {
    return null;
  }

  if (isDesktop) {
    return (
      <OverrideStatusPanelDesktopView {...modalProps} onClose={modalProps.onClose}>
        {body}
      </OverrideStatusPanelDesktopView>
    );
  }

  return (
    <OverrideStatusTrayMobileView {...modalProps} onClose={modalProps.onClose}>
      {body}
    </OverrideStatusTrayMobileView>
  );
};

const panelContentCss = css`
  padding: ${theme.spacing(3)};
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(2)};
`;

const statusListStyles = css`
  display: flex;
  flex-direction: column;
  gap: ${theme.spacing(1)};
`;

const statusRowContentStyles = css`
  display: flex;
  align-items: center;
  gap: ${theme.spacing(1)};
`;

const footerStyles = css`
  display: flex;
  justify-content: flex-end;
  gap: ${theme.spacing(1)};
  margin-top: auto;
  padding-top: ${theme.spacing(2)};
`;
